import { useCallback, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { CallIntelligenceV2, CallIntelTypes } from '@frontend/api-call-intel';
import { BackfillInternalBanner } from '@frontend/call-intel-backfill';
import { ContentSwitcher } from '@frontend/call-intel-core';
import { CallIntelProAnnouncementBanner, useProOnboardingPageIntent } from '@frontend/call-intel-onboarding';
import { Headers } from '@frontend/header';
import { useTranslation } from '@frontend/i18n';
import { Page } from '@frontend/page';
import { OnboardingHotspot, HOTSPOT_TARGET } from '@frontend/product-onboarding';
import { breakpoints } from '@frontend/responsiveness';
import { useNavigate, useSearch, type AnySearch } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Heading, NotificationBadge, Tabs, Text } from '@frontend/design-system';
import { CallIntelDemoBanner } from '../components/call-intelligence/call-intel-demo-banner';
import { CallIntelTrialHeaderAction } from '../components/call-intelligence/call-intel-trial-header-action';
import { CallIntelTrialHeaderChips } from '../components/call-intelligence/call-intel-trial-header-chips';
import { CallRecordingsBanner } from '../components/call-intelligence/call-recordings-banner';
import { CallIntelOverviewFilters } from '../components/call-intelligence/filters/organisms/call-intel-overview-filters';
import type { FilterValues } from '../components/call-intelligence/filters/types';
import { FiltersSection } from '../components/call-intelligence/filters-section';
import { FollowUpTasksByReasonList } from '../components/call-intelligence/follow-ups/follow-up-tasks-by-reason-list';
import { useActiveFilters } from '../components/call-intelligence/hooks/use-active-filters';
import { useCallIntelDemoFlags } from '../components/call-intelligence/hooks/use-call-intel-demo-flags';
import { useCallIntelLocations } from '../components/call-intelligence/hooks/use-call-intel-locations';
import {
  useCallIntelShallowStore,
  type MultiLocationsView,
} from '../components/call-intelligence/hooks/use-call-intel-store';
import { useFollowUpTabCount } from '../components/call-intelligence/hooks/use-follow-up-count';
import { useOfficeUsers } from '../components/call-intelligence/hooks/use-office-users';
import { CallIntelOverviewPanel } from '../components/call-intelligence/tab-panels/call-intel-overview-panel';
import { CallRecordsTable } from '../components/call-intelligence/tables/call-records-table';
import { SummaryCard } from '../components/call-intelligence/types';
import { FeedbackButton } from '../components/feedback-button';
import { ScrollClassTrigger } from '../components/scroll-class-trigger';
import { trackingIds } from '../tracking-ids';
import { callIntelResponsiveStyles } from './common-styles';

export const CallIntelligencePage = () => {
  const { t } = useTranslation('analytics');
  const pageRef = useRef<HTMLDivElement | null>(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [shouldSuppressBackfillBanner, setShouldSuppressBackfillBanner] = useState(false);
  const search = useSearch({ strict: false });
  const navigate = useNavigate();

  const tabsBarRef = useRef<HTMLDivElement | null>(null);

  const { isDemoAccount, isDemoModal } = useCallIntelDemoFlags();
  const {
    activeTab,
    activeMultiView = 'summary',
    filterHintText,
    isLoadingAggregatedData,
    setActiveTab,
    setActiveMultiView,
    setIsLoadingAggregatedData,
    setSubView,
  } = useCallIntelShallowStore(
    'activeTab',
    'activeMultiView',
    'filterHintText',
    'isLoadingAggregatedData',
    'setActiveTab',
    'setActiveMultiView',
    'setIsLoadingAggregatedData',
    'setSubView'
  );

  const { filters, allSelectedLocationIds, updateFilterValues, clearUrlOverrideFilters } = useActiveFilters();

  const { isMultiLocation, locations, locationIds } = useCallIntelLocations();

  const { isTaskCreationEnabled: shouldShowFollowUpTab } = CallIntelligenceV2.Hooks.useAggregateTaskSettings({
    locationIds,
    isDemo: isDemoAccount,
  });

  const { pendingFollowUpCount, totalTaskCount } = useFollowUpTabCount();

  const { activeOfficeUsersForFilter } = useOfficeUsers({
    isDemoLocation: filters.locations?.[0]?.includes('demo'),
    locationIds: filters.locations || [],
  });

  const locationIdsWithData = filters.locations || [];

  const getPageSubtitleText = () => {
    const dataLocationCount = locationIdsWithData.length;

    const locationText =
      isMultiLocation && filters.locations?.length
        ? dataLocationCount === Object.keys(locations).length
          ? t('all locations, ')
          : dataLocationCount === 1
          ? t('1 location, ')
          : t('{{count}} locations, ', { count: dataLocationCount })
        : '';

    return `${locationText}${filterHintText}`;
  };

  const handleClickOnSummaryCard = useCallback(
    (card: SummaryCard, locationId?: string) => {
      const filtersToUpdate: FilterValues = {};
      if (locationId) {
        filtersToUpdate.locations = [locationId];
      }

      if (card !== 'callsAnalyzed') {
        filtersToUpdate.schedulingOpportunity =
          CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_IDENTIFIED;

        if (card === 'unscheduled') {
          filtersToUpdate.schedulingOutcomes = [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNSCHEDULED];
        } else if (card === 'conversionRate') {
          filtersToUpdate.schedulingOutcomes = [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED];
        }
      }

      updateFilterValues(filtersToUpdate);
      setActiveTab('records');
    },
    [setActiveTab, updateFilterValues]
  );

  // Clear subView when main page mounts to ensure we use global filters
  useEffect(() => {
    setSubView({});
  }, []);

  useEffect(() => {
    if (search['submit-feedback']) {
      setShowFeedbackModal(true);
      const { 'submit-feedback': _, ...newSearch } = search;
      navigate({ search: newSearch as AnySearch, replace: true });
    }
  }, [search, navigate]);

  useEffect(() => {
    // Reset active tab when leaving the main page
    return () => {
      setActiveTab('overview');
      clearUrlOverrideFilters();
    };
  }, [setActiveTab, clearUrlOverrideFilters]);

  useEffect(() => {
    // If follow-up tab is not enabled and user is on follow-up tab, switch to overview tab
    if (!shouldShowFollowUpTab && activeTab === 'follow-up') {
      setActiveTab('overview');
    }
  }, [activeTab, setActiveTab, shouldShowFollowUpTab]);

  /**
   * Reset scroll position to top when active tab changes
   */
  useEffect(() => {
    const scrollableParent = pageRef.current?.parentElement;
    if (!scrollableParent) return;

    scrollableParent.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [activeTab]);

  useProOnboardingPageIntent({
    activeTab,
    summaryView: 'summary',
    setActiveTab,
    setActiveMultiView,
  });

  return (
    <>
      <CallIntelDemoBanner />
      <CallRecordingsBanner />

      {/* This is used to show a shadow on the tabs bar when user scrolls down the page
      In small mode header is sticky, so we need to detect when user scrolls past the header */}
      <ScrollClassTrigger targetRef={tabsBarRef} outOfViewClassName='sticky-shadow' />
      <Page css={callIntelResponsiveStyles.page} ref={pageRef} maxWidth='100%'>
        <Headers.Root css={callIntelResponsiveStyles.mainHeaderRoot} size='sm'>
          <Headers.RouterBreadcrumb />
          <Headers.TitleRow css={{ margin: 0 }}>
            <Headers.Title css={callIntelResponsiveStyles.mainHeaderTitle}>
              {isDemoModal ? (
                <Heading level={2}>{t('Call Intelligence')}</Heading>
              ) : (
                <div css={styles.titleWrapper}>
                  <Headers.RouterHeader title={t('Call Intelligence')} data-testid='router-header' />
                  <CallIntelTrialHeaderChips locationIds={filters.locations} />
                </div>
              )}
            </Headers.Title>

            <Headers.Actions css={styles.headerWrapper}>
              <FeedbackButton
                productCode={Feature.CALL_INTELLIGENCE}
                showModal={showFeedbackModal}
                trackingId={trackingIds.callIntel.generalFeedbackMainPage}
                responsive
              />
              {!isDemoModal && <CallIntelTrialHeaderAction locationIds={filters.locations || []} />}
            </Headers.Actions>
          </Headers.TitleRow>
          <Text
            size='large'
            as='div'
            css={callIntelResponsiveStyles.mainSubtitleText}
            data-testid='call-intel-subtitle'
          >
            {t('Showing results for {{filtersInfo}}', { filtersInfo: getPageSubtitleText() })}
          </Text>

          <CallIntelProAnnouncementBanner
            containerStyles={styles.inlineNotification}
            onVisibilityChange={setShouldSuppressBackfillBanner}
          />
          {!shouldSuppressBackfillBanner && (
            <BackfillInternalBanner locationIds={allSelectedLocationIds} customStyles={styles.inlineNotification} />
          )}
        </Headers.Root>
        <Page.Body>
          <Tabs
            controlledTab={activeTab}
            initialTab={activeTab}
            onChange={(id) => setActiveTab(id as CallIntelTypes.CallIntelTab)}
          >
            <div css={callIntelResponsiveStyles.tabsBarWrapper} ref={tabsBarRef} data-testid='tabs-bar'>
              <Tabs.Bar>
                <Tabs.Tab id='overview' trackingId={trackingIds.callIntel.overviewTab}>
                  {t('Overview')}
                </Tabs.Tab>
                {shouldShowFollowUpTab && (
                  <Tabs.Tab id='follow-up' trackingId={trackingIds.callIntel.followUpTab}>
                    <OnboardingHotspot
                      target={HOTSPOT_TARGET.CALL_INTEL_FOLLOW_UP_TASKS_TAB}
                      position='top-right'
                      offset={{ top: -4, right: -8 }}
                    >
                      <div css={styles.tabContentWrapper}>
                        {t('Follow-up Tasks')}
                        {pendingFollowUpCount > 0 && (
                          <NotificationBadge truncateCount data-testid='follow-up-count-badge'>
                            {pendingFollowUpCount}
                          </NotificationBadge>
                        )}
                      </div>
                    </OnboardingHotspot>
                  </Tabs.Tab>
                )}
                <Tabs.Tab id='records' trackingId={trackingIds.callIntel.recordsTab}>
                  {t('All Call Recordings')}
                </Tabs.Tab>
              </Tabs.Bar>
            </div>
            {['overview', 'follow-up'].includes(activeTab) && (
              <FiltersSection wrapperCss={styles.filtersWrapperWithTabs} data-testid='call-intel-filters-section'>
                <CallIntelOverviewFilters isLoading={isLoadingAggregatedData}>
                  <CallIntelOverviewFilters.Filters
                    officeUsers={activeOfficeUsersForFilter}
                    taskCount={totalTaskCount}
                    showTaskArchivalBanner={activeTab === 'follow-up'}
                  />
                  <CallIntelOverviewFilters.Actions>
                    {isMultiLocation && activeTab === 'overview' && (
                      <div
                        css={callIntelResponsiveStyles.multiComparison}
                        data-testid='multi-comparison-content-switcher'
                      >
                        <ContentSwitcher<MultiLocationsView>
                          defaultValue={activeMultiView}
                          onValueChange={setActiveMultiView}
                        >
                          <ContentSwitcher.Option
                            label={t('Summary View')}
                            iconName='building-small'
                            value='summary'
                            trackingId='summary-view'
                          />
                          <ContentSwitcher.Option
                            label={t('Locations View')}
                            iconName='locations-small'
                            value='betweenLocations'
                            trackingId='locations-view'
                          />
                        </ContentSwitcher>
                      </div>
                    )}
                  </CallIntelOverviewFilters.Actions>
                </CallIntelOverviewFilters>
              </FiltersSection>
            )}
            <div css={callIntelResponsiveStyles.tabPanelWrapper}>
              <Tabs.Panel controller='overview' css={styles.chartsTabPanel}>
                {activeTab === 'overview' && (
                  <CallIntelOverviewPanel
                    onChangeLoadingState={setIsLoadingAggregatedData}
                    onClickSummaryCard={handleClickOnSummaryCard}
                  />
                )}
              </Tabs.Panel>
              <Tabs.Panel controller='follow-up' css={styles.recordsTablePanel}>
                {activeTab === 'follow-up' && shouldShowFollowUpTab && <FollowUpTasksByReasonList />}
              </Tabs.Panel>
              <Tabs.Panel controller='records' css={styles.recordsTablePanel}>
                {activeTab === 'records' && <CallRecordsTable isDemoAccount={isDemoAccount} marginTop={0} />}
              </Tabs.Panel>
            </div>
          </Tabs>
        </Page.Body>
      </Page>
    </>
  );
};

const styles = {
  chartsTabPanel: css`
    padding: ${theme.spacing(1, 0)};
  `,
  recordsTablePanel: css`
    flex-grow: 1;
    padding-top: ${theme.spacing(1)};
  `,
  tabContentWrapper: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  filtersWrapperWithTabs: css`
    top: 44px; // Custom top offset for filters when used with tabs
    @container (max-width: ${breakpoints.xsmall.max}px) {
      padding: ${theme.spacing(2)};
    }
  `,
  titleWrapper: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(2)};
  `,
  inlineNotification: css`
    margin-top: ${theme.spacing(2)};
    max-width: none;
    max-height: none;
  `,
  headerWrapper: css`
    gap: ${theme.spacing(2)};
  `,
};
